import type { CloudBaseSession, CloudBaseWebClient } from "./cloudBaseWebClient";

/** Compatibility interface for the existing modules; all traffic stays on ECS. */
export function createEcsWebClient(baseUrl: string, fetcher: typeof fetch = fetch): CloudBaseWebClient {
  const base = baseUrl.replace(/\/+$/, "");
  let session: CloudBaseSession | null = null;

  async function request(path: string, body?: unknown, accessToken?: string) {
    const response = await fetcher(`${base}${path}`, {
      method: body === undefined ? "GET" : "POST",
      credentials: "include",
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const payload = await response.json();
    if (!response.ok) throw Object.assign(new Error(payload.message ?? "服务暂时不可用"), { code: payload.code });
    return payload;
  }

  async function accessToken() {
    const result = await request("/auth/session");
    session = result.session;
    if (!session?.access_token) throw Object.assign(new Error("登录状态已失效"), { code: "UNAUTHORIZED" });
    return session.access_token;
  }

  return {
    auth: {
      async getSession() {
        try {
          const result = await request("/auth/session");
          session = result.session;
          return { data: { session }, error: null };
        } catch (error) {
          session = null;
          return { data: { session: null }, error };
        }
      },
      async signInWithPassword(input) {
        try {
          const result = await request("/auth/login", input);
          session = result.session;
          return { data: { session }, error: null };
        } catch (error) {
          return { data: { session: null }, error };
        }
      },
      async signOut() {
        try { await request("/auth/logout", {}); } finally { session = null; }
      },
    },
    rdb: () => ({
      async rpc(name, parameters = {}) {
        try {
          return { data: await request(`/rpc/${encodeURIComponent(name)}`, parameters, await accessToken()), error: null };
        } catch (error) {
          return { data: null, error: { code: error instanceof Error && "code" in error ? String(error.code) : "NETWORK_ERROR", message: error instanceof Error ? error.message : "服务暂时不可用" } };
        }
      },
    }),
    async callFunction<T>({ name, data }: { name: string; data: Record<string, unknown>; parse: true }) {
      const result = await request(`/functions/${encodeURIComponent(name)}`, data, await accessToken());
      return { requestId: String(result.request_id ?? ""), result: result as T };
    },
  };
}
